import React, {useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useQuery} from 'react-query';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import * as Actions from '../../store/actions';
import {
  DashboardLayout,
  PrimaryButton,
  TableLayout,
  Pagination,
  PageLimit,
  Loader,
  SecondaryButton,
  SearchInput,
} from '../../components';
import {eye, lookupEmpty} from '../../assets/images/images';
import VerificationFormModal from './VerificationFormModal';
import ViewVerificationModal from './ViewVerificationModal';
import {
  getStatusColor,
  filterOutEmptyValues,
  generateQueryParams,
} from '../../helpers';
import {
  fetchAddressVerifications,
  fetchAddressVerificationSummary,
} from '../../requests/queries/address-verification';
import {toast} from 'react-toastify';
import uppercase from 'lodash/upperCase';
import BatchUploadModal from './BatchUploadModal';
import CardSummary from '../../components/CardSummary';
import {EmptyFilterState} from '../../components/EmptyFilterState';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';
import {dateRanges} from 'helpers/getDateRange';

const tableHeader = [
  {
    label: 'Name',
    key: 'first_name',
  },
  {
    label: 'Phone number',
    key: 'phone_number',
  },
  {
    label: 'Verification type',
    key: 'model_type',
  },
  {
    label: 'status',
    key: 'status',
  },
  {
    label: 'Date created',
    key: 'date_created',
  },
];

function AddressVerification({auth: {userDetails}}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [openBatch, setOpenBatch] = useState(false);
  const [openVerificationModal, setOpenVerificationModal] = useState(false);
  const [filterValues, setFilterValues] = useState({
    date: 'seven_days',
    dateRange: dateRanges.seven_days(),
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [selectedData, setSelectedData] = useState(null);

  const params = useMemo(
    () => ({
      page: currentPage,
      limit,
      search: searchText,
      status: filterValues.status,
      ...filterValues.dateRange,
    }),
    [
      currentPage,
      filterValues.dateRange,
      filterValues.status,
      limit,
      searchText,
    ],
  );

  const {data: addressVerifications, isLoading} = useQuery(
    ['address-verifications', params],
    () => fetchAddressVerifications({...params}),
    {
      onSuccess: data => {
        toast.success(data.message);
      },
      onError: error => {
        toast.error(error.response?.data?.error || 'Failed to fetch data');
      },
      enabled: !!userDetails,
    },
  );

  const {
    data: addressVerificationSummary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery(['address-verification-summary', filterValues], () =>
    fetchAddressVerificationSummary({
      status: filterValues.status,
      ...filterValues.dateRange,
    }),
  );

  const refreshSummary = () => {
    refetchSummary();
  };

  const completedCount = parseInt(
    (
      addressVerificationSummary?.data.find(
        item => item.status === 'completed',
      ) || {}
    ).count || 0,
  );

  const failedCount = parseInt(
    (
      addressVerificationSummary?.data.find(item => item.status === 'failed') ||
      {}
    ).count || 0,
  );

  const pendingCount = parseInt(
    (
      addressVerificationSummary?.data.find(
        item => item.status === 'pending',
      ) || {}
    ).count || 0,
  );
  const totalCount = completedCount + failedCount + pendingCount;

  const openModalWithSelectedData = data => {
    setSelectedData(data);
    setOpenVerificationModal(true);
  };

  const closeModal = () => {
    setOpenVerificationModal(false);
  };

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const data = !isLoading && addressVerifications?.data;

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  const handleFilter = (selectedOptions, shouldDownload = false) => {
    const filteredOptions = filterOutEmptyValues(selectedOptions);

    const queryParams = generateQueryParams({
      ...filteredOptions,
      start: filteredOptions.dateRange?.start,
      end: filteredOptions.dateRange?.end,
    });

    const apiFilters = {
      ...filteredOptions,
      status: filteredOptions.status,
      dateRange: filteredOptions.dateRange,
    };

    if (shouldDownload) {
      apiFilters.download = true;
      fetchAddressVerifications({
        page: currentPage,
        limit,
        search: searchText,
        ...apiFilters,
      })
        .then(response => {
          toast.success('Export started successfully');
          if (response.data && response.data instanceof Blob) {
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'address_verifications.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        })
        .catch(error => {
          toast.error(error.response?.data?.error || 'Failed to export data');
        });
    } else {
      setFilterValues(apiFilters);
      setCurrentPage(1);
      navigate(queryParams ? `?${queryParams}` : '');
    }
  };

  const {
    performIndividualLookupAndViewHistory,
    downloadIndividualHistory,
    performBatchLookupAndViewHistory,
  } = useMemo(
    () => userDetails?.permissions?.addressVerificationPermissions || {},
    [userDetails],
  );

  const renderEmptyState = () =>
    isLoading ? (
      <tbody>
        <tr>
          <td colSpan={8}>
            <Loader height={50} />
          </td>
        </tr>
      </tbody>
    ) : !performIndividualLookupAndViewHistory ? (
      <EmptyFilterState
        height={45}
        body="Opps!! sorry, you do not have access to this resource"
        src={lookupEmpty}
        noBtn
      />
    ) : data?.length === 0 && !isLoading ? (
      <EmptyFilterState
        height={45}
        body="Perform multiple address verification with ease"
        src={lookupEmpty}
        customBtn={
          <div className="flex items-center gap-4">
            <SecondaryButton
              buttonText="Upload Batch Addresses"
              onClick={() => setOpenBatch(!openBatch)}
              className="w-fit"
              disabled={!performBatchLookupAndViewHistory}
            />
            <PrimaryButton
              buttonText="Submit Address"
              className="w-fit"
              onClick={() => setOpen(!open)}
              disabled={!performIndividualLookupAndViewHistory}
            />
          </div>
        }
      />
    ) : null;

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">
          Address Verification
        </p>
      }
    >
      <ViewVerificationModal
        open={openVerificationModal}
        setOpen={closeModal}
        selectedData={selectedData}
      />
      <VerificationFormModal
        open={open}
        setOpen={setOpen}
        onVerificationSuccess={refreshSummary}
      />

      <div className="flex items-center py-2 flex-wrap gap-3 justify-between mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            onChange={value => setSearchText(value)}
            placeholder="Search by name or phone number"
          />
          <SecondaryButton
            buttonText="Filter"
            onClick={toggleFilterVisibility}
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <SecondaryButton
            buttonText="Export"
            onClick={() => handleFilter(filterValues, true)}
            disabled={!downloadIndividualHistory}
          />
          <SecondaryButton
            buttonText="Upload Batch Addresses"
            onClick={() => setOpenBatch(!openBatch)}
            disabled={!performBatchLookupAndViewHistory}
            style={{
              cursor: !userDetails?.company?.verified && 'not-allowed',
            }}
          />
          <PrimaryButton
            buttonText="Submit Address"
            onClick={() => setOpen(!open)}
            className="hidden lg:flex"
            disabled={!performIndividualLookupAndViewHistory}
          />
          <BatchUploadModal open={openBatch} setOpen={setOpenBatch} />
        </div>
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2
          filters={[
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: [
                {value: 'pending', label: 'Pending'},
                {value: 'completed', label: 'Completed'},
                {value: 'failed', label: 'Failed'},
              ],
              multiSelect: false,
            },
          ]}
          onFilterChange={handleFilter}
          initialValues={filterValues}
        />
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 z-10 px-4 py-2 overflow-x-hidden sm:hidden bg-white80"
        style={{
          boxShadow: '0px -1px 8px rgba(51, 52, 66, 0.25)',
        }}
      >
        <PrimaryButton
          buttonText="Submit Address"
          xPadding="p-[35px]"
          onClick={() => setOpen(!open)}
          className="w-full bottom-2"
          disabled={!performIndividualLookupAndViewHistory}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-center gap-4 py-6">
        <CardSummary title="Total" body={totalCount} loading={summaryLoading} />
        <CardSummary
          title="Pending"
          body={pendingCount}
          loading={summaryLoading}
        />
        <CardSummary
          title="Completed"
          body={completedCount}
          loading={summaryLoading}
        />
        <CardSummary
          title="Failed"
          body={failedCount}
          loading={summaryLoading}
        />
      </div>
      <>
        {performIndividualLookupAndViewHistory && (
          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                {tableHeader.map(({label, key}) => (
                  <th
                    key={key}
                    className={`${
                      label === 'Name'
                        ? 'pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap'
                        : 'pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap'
                    }`}
                  >
                    <div className="flex items-end gap-1 cursor-pointer">
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>

            {renderEmptyState()}

            <tbody className="text-sm bg-white">
              {!isLoading &&
                data?.map(verification => (
                  <tr
                    className="border-[0.5px] border-[#E4E3EC]"
                    key={verification.id}
                  >
                    <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                      {verification.first_name} {verification.last_name}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {verification.mobile ?? '-'}
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      <div className="px-2 pt-[5px] pb-[3px] text-xs font-medium uppercase rounded-full text-success bg-success20 w-fit">
                        individual
                      </div>
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      <div
                        className={`px-2 pt-[5px] pb-[3px] text-xs font-medium uppercase rounded-full w-fit ${getStatusColor(
                          verification.status,
                        )}`}
                      >
                        {uppercase(verification.status)}
                      </div>
                    </td>
                    <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                      {moment(verification?.date_created).format(
                        'Do MMM, YYYY h:mm A',
                      )}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        {verification.status === 'pending' ? null : (
                          <button
                            title="View"
                            onClick={() =>
                              openModalWithSelectedData(verification)
                            }
                            className="h-4 aspect-square"
                          >
                            <img src={eye} alt="" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </TableLayout>
        )}

        {data?.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
            <PageNumber
              currentPage={+currentPage}
              limit={limit}
              totalCount={addressVerifications?.totalRecords}
            />
            <div className="flex items-center gap-5">
              <PageLimit
                onLimitChange={setLimit}
                total={
                  addressVerifications && addressVerifications?.totalRecords
                }
                length={data?.length}
                limit={limit}
              />
              <Pagination
                total={
                  data && Math.ceil(addressVerifications?.totalRecords / limit)
                }
                current={+currentPage}
                onPageChange={activePage => {
                  pageClick(activePage);
                }}
              />
            </div>
          </div>
        )}
      </>
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(AddressVerification);
